import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

export const ProjectsSection = () => {
  const { t } = useTranslation();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  const projects = t('projects.items', { returnObjects: true });

  return (
    <section
      id="projects"
      data-testid="projects-section"
      ref={ref}
      className="py-16 md:py-24 lg:py-32 px-4 md:px-6 lg:px-8 bg-[#0F172A]"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 md:mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold text-[#F8FAFC] mb-4">
            {t('projects.title')}
          </h2>
          <p className="text-[#CBD5E1] text-lg md:text-xl">
            {t('projects.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {projects.map((project, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <Card
                data-testid="project-card"
                className="bg-[#1A1F3A] rounded-2xl overflow-hidden border border-[#22D3EE]/20 hover:border-[#22D3EE]/50 hover:shadow-[0_0_40px_rgba(34,211,238,0.2)] hover:-translate-y-2 transition-all duration-400 h-full flex flex-col"
              >
                <CardContent className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between mb-4">
                    <h3 className="text-xl font-semibold text-[#F8FAFC] flex-1">
                      {project.name}
                    </h3>
                    <ExternalLink className="w-5 h-5 text-[#22D3EE] flex-shrink-0 ml-2" />
                  </div>

                  <p className="text-[#CBD5E1] text-sm leading-relaxed mb-6 flex-1">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <Badge
                        key={techIndex}
                        data-testid="skill-badge"
                        className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/30 hover:bg-[#22D3EE]/20 hover:border-[#22D3EE]/50 transition-all duration-200"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
